import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { FieldHint, FieldLabel, fieldClass } from '@/client/pages/Admin/shared/form-field';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useGate } from '@/client/hooks/useGate';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useAppsCatalogSectionsQuery } from '@/client/api/apps-catalog/apps-catalog.query';
import {
  getAppsCatalogFieldErrors,
  useCreateAppsCatalogSectionMutation,
  useReorderAppsCatalogSectionsMutation,
  useUpdateAppsCatalogSectionMutation,
} from '@/client/api/apps-catalog/apps-catalog.mutation';
import type { AppCatalogSection } from '@/client/types/apps-catalog';
import { AppsCatalogHeader } from '@/client/components/admin/AppsCatalog/AppsCatalogHeader';
import { APPS_CATALOG_PERMS } from '@/client/components/admin/AppsCatalog/catalogUi';

const KEY_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const KEY_MAX_LEN = 64;

type DialogState = { mode: 'create' } | { mode: 'rename'; section: AppCatalogSection } | null;

const SectionDialog = ({ state, onClose }: { state: DialogState; onClose: () => void }) => {
  const createMutation = useCreateAppsCatalogSectionMutation();
  const updateMutation = useUpdateAppsCatalogSectionMutation();
  const saving = createMutation.isPending || updateMutation.isPending;

  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [visible, setVisible] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!state) return;
    setKey(state.mode === 'rename' ? state.section.key : '');
    setLabel(state.mode === 'rename' ? state.section.label : '');
    setVisible(true);
    setErrors({});
  }, [state]);

  const submit = async () => {
    if (!state) return;
    const clientErrors: Record<string, string> = {};
    const trimmedKey = key.trim();
    const trimmedLabel = label.trim();
    if (state.mode === 'create') {
      if (!trimmedKey) clientErrors.key = 'required';
      else if (trimmedKey.length > KEY_MAX_LEN || !KEY_RE.test(trimmedKey))
        clientErrors.key = `lowercase letters and digits joined by single hyphens, at most ${KEY_MAX_LEN} characters`;
    }
    if (!trimmedLabel) clientErrors.label = 'required';
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    try {
      if (state.mode === 'create') {
        await createMutation.mutateAsync({ key: trimmedKey, label: trimmedLabel, visible });
        toast.success(`Section ${trimmedLabel} created`);
      } else {
        if (trimmedLabel === state.section.label) {
          onClose();
          return;
        }
        await updateMutation.mutateAsync({ key: state.section.key, changes: { label: trimmedLabel } });
        toast.success('Section renamed');
      }
      onClose();
    } catch (error) {
      setErrors(getAppsCatalogFieldErrors(error));
      toast.error(state.mode === 'create' ? 'Create failed' : 'Rename failed', {
        description: getApiErrorMessage(error, 'Something went wrong.'),
      });
    }
  };

  const otherErrors = Object.entries(errors).filter(([k]) => k !== 'key' && k !== 'label');

  return (
    <Dialog open={state != null} onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {state?.mode === 'rename' ? `Rename ${state.section.label}` : 'New section'}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4 pt-2"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="space-y-1.5">
            <FieldLabel required={state?.mode === 'create'}>Key</FieldLabel>
            <Input
              className={fieldClass('font-mono', errors.key)}
              value={key}
              maxLength={KEY_MAX_LEN}
              placeholder="locals"
              disabled={state?.mode === 'rename'}
              onChange={(e) => {
                setKey(e.target.value.toLowerCase());
                setErrors((prev) => ({ ...prev, key: '' }));
              }}
              aria-invalid={!!errors.key}
            />
            <FieldHint error={errors.key} />
            <p className="text-[11px] text-gray-500">The key cannot change after create.</p>
          </div>
          <div className="space-y-1.5">
            <FieldLabel required>Label</FieldLabel>
            <Input
              className={fieldClass('', errors.label)}
              value={label}
              maxLength={100}
              placeholder="Locals"
              autoFocus
              onChange={(e) => {
                setLabel(e.target.value);
                setErrors((prev) => ({ ...prev, label: '' }));
              }}
              aria-invalid={!!errors.label}
            />
            <FieldHint error={errors.label} />
          </div>
          {state?.mode === 'create' && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={visible}
                onChange={(e) => setVisible(e.target.checked)}
              />
              Visible in the public catalog
            </label>
          )}
          {otherErrors.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
              {otherErrors.map(([k, reason]) => (
                <p key={k}>
                  {k}: {reason}
                </p>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : state?.mode === 'rename' ? 'Rename' : 'Create section'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const HEADINGS = ['Order', 'Label', 'Key', 'Sort order', 'Visible', 'Apps', 'Actions'];

const AppsCatalogSectionsPage = () => {
  useBreadcrumbs([{ label: 'Apps catalog', href: '/admin/apps-catalog' }, { label: 'Sections' }]);

  const { data: sections = [], isLoading, isError, error, isFetching } = useAppsCatalogSectionsQuery();
  const updateMutation = useUpdateAppsCatalogSectionMutation();
  const reorderMutation = useReorderAppsCatalogSectionsMutation();
  const writeGate = useGate(APPS_CATALOG_PERMS.write);

  const [dialog, setDialog] = useState<DialogState>(null);
  const [hideTarget, setHideTarget] = useState<AppCatalogSection | null>(null);

  const reorderBusy = reorderMutation.isPending || isFetching || !writeGate.allowed;

  const move = async (index: number, dir: -1 | 1) => {
    const other = index + dir;
    if (other < 0 || other >= sections.length) return;
    const keys = sections.map((section) => section.key);
    [keys[index], keys[other]] = [keys[other], keys[index]];
    try {
      await reorderMutation.mutateAsync(keys);
      toast.success('Order updated');
    } catch (err) {
      toast.error('Reorder failed', { description: getApiErrorMessage(err, 'Something went wrong.') });
    }
  };

  const setVisible = async (section: AppCatalogSection, visible: boolean) => {
    const toastId = toast.loading(visible ? 'Showing section…' : 'Hiding section…');
    try {
      await updateMutation.mutateAsync({ key: section.key, changes: { visible } });
      toast.success(visible ? `${section.label} is visible` : `${section.label} is hidden`, { id: toastId });
      setHideTarget(null);
    } catch (err) {
      toast.error('Update failed', {
        id: toastId,
        description: getApiErrorMessage(err, 'Something went wrong.'),
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <AppsCatalogHeader
          active="sections"
          description="Sections group apps in the header grid and the store, in this order. A hidden section and all its apps leave the public catalog."
          actions={
            <PermissionButton
              code={APPS_CATALOG_PERMS.write}
              className="gap-1.5"
              onClick={() => setDialog({ mode: 'create' })}
            >
              <Plus className="h-4 w-4" />
              New section
            </PermissionButton>
          }
        />

        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {HEADINGS.map((heading) => (
                  <th
                    key={heading}
                    className={`whitespace-nowrap px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                      heading === 'Actions' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading &&
                Array.from({ length: 5 }).map((_, row) => (
                  <tr key={row}>
                    {HEADINGS.map((heading) => (
                      <td key={heading} className="px-4 py-4">
                        <Skeleton className="h-4 w-20" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={HEADINGS.length} className="px-6 py-12 text-center text-sm text-red-600">
                    {getApiErrorMessage(error, 'Could not load sections.')}
                  </td>
                </tr>
              )}

              {!isLoading && !isError && sections.length === 0 && (
                <tr>
                  <td colSpan={HEADINGS.length} className="px-6 py-12 text-center text-sm text-gray-500">
                    No sections yet.
                  </td>
                </tr>
              )}

              {!isLoading &&
                !isError &&
                sections.map((section, index) => (
                  <tr key={section.key} className={section.visible ? 'hover:bg-blue-50/40' : 'bg-gray-50/70'}>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center gap-0.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          disabled={index === 0 || reorderBusy}
                          onClick={() => void move(index, -1)}
                          title={writeGate.allowed ? 'Move up' : writeGate.reason}
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          disabled={index >= sections.length - 1 || reorderBusy}
                          onClick={() => void move(index, 1)}
                          title={writeGate.allowed ? 'Move down' : writeGate.reason}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{section.label}</td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-700">{section.key}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-600">
                      {section.sort_order}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          section.visible ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {section.visible ? 'Visible' : 'Hidden'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <Link
                        to={`/admin/apps-catalog?section_key=${encodeURIComponent(section.key)}`}
                        className="font-medium text-blue-700 hover:underline"
                      >
                        {section.app_count ?? 0} {section.app_count === 1 ? 'app' : 'apps'}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <PermissionButton
                          code={APPS_CATALOG_PERMS.write}
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          onClick={() => setDialog({ mode: 'rename', section })}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Rename
                        </PermissionButton>
                        <PermissionButton
                          code={APPS_CATALOG_PERMS.write}
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          disabled={updateMutation.isPending}
                          onClick={() =>
                            section.visible ? setHideTarget(section) : void setVisible(section, true)
                          }
                        >
                          {section.visible ? (
                            <>
                              <EyeOff className="h-3.5 w-3.5" />
                              Hide
                            </>
                          ) : (
                            <>
                              <Eye className="h-3.5 w-3.5" />
                              Show
                            </>
                          )}
                        </PermissionButton>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <SectionDialog state={dialog} onClose={() => setDialog(null)} />

      <AlertDialog
        open={hideTarget != null}
        onOpenChange={(open) => {
          if (!open && !updateMutation.isPending) setHideTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hide {hideTarget?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              The section and its {hideTarget?.app_count ?? 0} app
              {hideTarget?.app_count === 1 ? '' : 's'} leave the header grid and the store on every
              site. The apps keep their status and come back when the section is shown again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (hideTarget) void setVisible(hideTarget, false);
              }}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Hiding…' : 'Hide section'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AppsCatalogSectionsPage;
